import { CastorService, FieldService } from '@aipb/gulliver/castor/data-access';
import { ProjectCoreLang } from '@aipb/gulliver/castor/model';
import { CatalogEnvService } from '@aipb/gulliver/catalog/env';
import { FrameService } from '@aipb/gulliver/frame/data-access';
import { AuthService } from '@aipb/shared/auth';
import { isNil } from '@aipb/shared/util/other';
import { Language } from '@aipb/shared/util/translation';
import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize, mergeMap, tap } from 'rxjs/operators';
import { CastorConfigRepository } from './castor-config.repository';

const DEFAULT_CORE_REGEX = /default_core_(.*)/;

@Injectable({
  providedIn: 'root'
})
export class CastorConfigService {
  constructor(
    private transloco: TranslocoService,
    private castor: CastorService,
    private repository: CastorConfigRepository,
    private fieldService: FieldService,
    private auth: AuthService,
    private catalogEnv: CatalogEnvService,
    private frame: FrameService
  ) {}

  get configId() {
    return this.catalogEnv.activeConfigId;
  }

  load(first = false) {
    const lang = this.transloco.getActiveLang();

    this.frame.setLoading(true);
    return forkJoin([
      this.castor.detailReportLang(lang),
      this.castor.projectInfoLang({
        uiLang: lang
      })
    ])
      .pipe(
        finalize(() => {
          this.frame.setLoading(false);
        }),
        mergeMap(([reports, projectInfo]) => {
          const activeCoreId = this.repository.getActiveCoreId();
          const cores = projectInfo.projectCores; //.filter((c) => this.permission.catalog.canUseCastorCore(c.coreId));

          if (first) {
            if (!cores.some((c) => c.coreId === activeCoreId)) {
              this.repository.setActiveCoreId(this.catalogEnv.env.castor.defaultCoreId);
            }

            const defaultCoreRole = this.auth.getRole((role) => DEFAULT_CORE_REGEX.test(role));

            if (defaultCoreRole) {
              const match = defaultCoreRole.match(DEFAULT_CORE_REGEX);
              const defaultCore = match[1];
              this.repository.setActiveCoreId(defaultCore);
            }

            if (!this.auth.isLoggedIn()) {
              this.repository.setActiveCoreId(this.catalogEnv.env.castor.defaultCoreId);
            }
          }

          this.repository.reports = reports.sort((a, b) => a.reportId.localeCompare(b.reportId));
          this.repository.cores = cores;

          return forkJoin(
            projectInfo.projectCores.map((pc) =>
              this.castor
                .coreInfoLang({
                  coreId: pc.coreId,
                  uiLang: Language.getLanguageByIso_639_1(lang).iso_639_1
                })
                .pipe(
                  catchError(() => {
                    return of(null);
                  })
                )
            )
          ).pipe(
            tap((coreInfos) => {
              const cores: ProjectCoreLang[] = [];
              coreInfos
                .filter((ci) => ci)
                .forEach((ci) => {
                  this.fieldService.setup(
                    ci,
                    isNil(this.catalogEnv.env.castor.sortRegisterFields)
                      ? true
                      : this.catalogEnv.env.castor.sortRegisterFields
                  );
                  cores.push(ci);
                });
              this.repository.cores = cores;
            })
          );
        })
      )
      .subscribe();
  }
}
